import { useHandler } from "@redotech/react-util/hook";
import { ReturnProduct } from "@redotech/redo-model/draft-return/draft-return-items";
import { calculateOfferings } from "@redotech/redo-model/extended-warranties/calculate-offerings";
import {
  CollectionSettings,
  EWWidgetTextProps,
  ExtendedWarrantyDataTarget,
  ExtendedWarrantyPdpNoCoverageType,
  ExtendedWarrantyTilesSize,
  Offering,
  OfferingSelection,
} from "@redotech/redo-model/extended-warranty";
import { Step } from "@redotech/redo-model/return-flow";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import * as warrantyStyles from "@redotech/redo-web/expanded-warranties/extended-warranty.module.css";
import { OfferingTiles } from "@redotech/redo-web/expanded-warranties/offering-tiles";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import * as classNames from "classnames";
import htmlReactParser from "html-react-parser";
import { memo, useEffect, useMemo, useState } from "react";

export const EwUpsellStep = memo(function EwUpsellStep({
  step,
  product,
  collectionSettings,
  submit,
  text,
}: {
  step: Step.EWUpsell;
  product: ReturnProduct;
  collectionSettings: CollectionSettings[] | undefined;
  submit: (selectedOffering?: OfferingSelection) => void;
  text: EWWidgetTextProps;
}) {
  // TODO: Replace with experience design
  const {
    modalTitle,
    modalDescription,
    modalCoverageHeader,
    modalBulletPoints,
    modalPlanHeader,
    pdpCoverageDisabledLabel,
    pdpLabel,
    pdpNoCoveragePrice,
  } = text;

  const [selectedOffering, setSelectedOffering] = useState<
    Offering | undefined
  >(undefined);

  const offerings = useMemo(() => {
    if (!collectionSettings) return undefined;

    return calculateOfferings({
      collectionOfferings: collectionSettings,
      variant: {
        // Expects price to be in cents
        price: Number(product.price) * 100,
        variantId: product.variantId,
        collections: product.collections.map((collection) => ({
          title: collection,
          // TODO: Make return product collections include handle
          handle: "unknown",
        })),
      },
    });
  }, [product, collectionSettings]);

  const handleTileClick = useHandler((offering: Offering) => {
    setSelectedOffering(offering);
  });

  const handleDecline = useHandler(() => {
    submit(undefined);
  });

  const handleAccept = useHandler(() => {
    if (!selectedOffering) {
      throw new Error("Selected offering is undefined");
    }
    submit({
      collectionHandle: selectedOffering.collectionHandle,
      timeId: selectedOffering.id,
    });
  });

  useEffect(() => {
    if (offerings) {
      if (offerings.length === 0) {
        submit(undefined);
      } else {
        setSelectedOffering(offerings[0]);
      }
    }
  }, [offerings, submit, setSelectedOffering]);

  if (!offerings) return null;

  return (
    <Flex
      className={warrantyStyles.extendedWarrantyModal}
      data-target={ExtendedWarrantyDataTarget.MODAL_CONTAINER}
      flexDirection="column"
      w="full"
    >
      <Flex
        data-target={ExtendedWarrantyDataTarget.MODAL_CONTENT_1}
        flexDirection="column"
        gap="sm"
      >
        <Text fontSize="xl" fontWeight="semibold">
          {htmlReactParser(modalTitle)}
        </Text>
        <Text fontSize="sm" fontWeight="thin">
          {htmlReactParser(modalDescription)}
        </Text>
      </Flex>
      {modalBulletPoints.length > 0 && (
        <Flex
          data-target={ExtendedWarrantyDataTarget.MODAL_CONTENT_2}
          flexDirection="column"
          gap="sm"
        >
          <Text fontSize="md" fontWeight="semibold">
            {htmlReactParser(modalCoverageHeader)}
          </Text>
          <ul
            className={classNames(
              warrantyStyles.bullets,
              warrantyStyles.subtitle,
            )}
            data-target={ExtendedWarrantyDataTarget.MODAL_CONTENT_2_LIST}
          >
            {modalBulletPoints.map((bulletPoint, index) => (
              <li
                data-target={
                  ExtendedWarrantyDataTarget.MODAL_CONTENT_2_LIST_ITEM
                }
                key={index}
              >
                <Text fontSize="sm" fontWeight="thin">
                  {htmlReactParser(bulletPoint)}
                </Text>
              </li>
            ))}
          </ul>
        </Flex>
      )}
      <Flex
        data-target={ExtendedWarrantyDataTarget.MODAL_CONTENT_3}
        flexDirection="column"
        gap="xs"
      >
        <Text fontSize="md" fontWeight="semibold">
          {htmlReactParser(modalPlanHeader)}
        </Text>
        <OfferingTiles
          excludeNoCoverage
          offerings={offerings}
          onTileClick={handleTileClick}
          selectedOffering={selectedOffering}
          tilesSize={ExtendedWarrantyTilesSize.REGULAR}
          view={{
            noCoverageLabel: pdpCoverageDisabledLabel,
            pdpLabel,
            pdpNoCoverageType: ExtendedWarrantyPdpNoCoverageType.BASIC,
            pdpNoCoveragePrice,
          }}
        />
      </Flex>
      <Flex
        data-target={ExtendedWarrantyDataTarget.MODAL_BUTTON_CONTAINER}
        gap="xs"
        justify="flex-end"
        mt="auto"
        pt="3xl"
      >
        <RedoButton
          hierarchy="link"
          onClick={handleDecline}
          size="md"
          text="Decline coverage"
        />
        <RedoButton
          hierarchy="primary"
          onClick={handleAccept}
          size="md"
          text="Add protection"
        />
      </Flex>
    </Flex>
  );
});
